/**
 * Learns how long a gap between bursts of activity may run before it means
 * "the burst is over", from the gaps a source actually produces. Domain-free:
 * the caller decides what a burst is (voiced audio, keystrokes, touches,
 * cursor movement) and reports gaps; this decides the threshold.
 *
 * Model: gaps observed *inside* a burst — silence that activity then resumed
 * across — are the source's within-burst gaps. The threshold tracks a
 * multiple of their upper range (p90 of the recent history), clamped to
 * [min, max]. It starts at `initialMs` and only moves once at least
 * `minObservations` gaps have been seen.
 *
 * Censoring: a gap longer than the current threshold ends the burst, so it is
 * never observed as within-burst — the estimator could only ever shrink. The
 * caller closes that loop with `observeResume(gapMs)`: when activity resumes
 * within `resumeGraceMs` of a close, the close split a burst, and the gap is
 * learned as a within-burst gap after all. This is the path by which a
 * halting source pushes the threshold up.
 *
 * Every observation and result is in milliseconds; nothing here reads a clock.
 */
export interface AdaptiveGapThresholdOptions {
  /** Threshold before any gaps have been observed. */
  initialMs: number
  minMs: number
  maxMs: number
  /** How soon after a close a resume still counts as the same burst. */
  resumeGraceMs: number
  /**
   * Observed within-burst gaps are a lower bound on the real ones (see
   * censoring), so the threshold sits this far above their upper range.
   */
  multiplier?: number
  /** Gaps shorter than this are jitter, not a pause worth learning from. */
  minLearnableGapMs?: number
  historySize?: number
  minObservations?: number
}

export class AdaptiveGapThreshold {
  private readonly initialMs: number
  private readonly minMs: number
  private readonly maxMs: number
  private readonly resumeGraceMs: number
  private readonly multiplier: number
  private readonly minLearnableGapMs: number
  private readonly historySize: number
  private readonly minObservations: number

  private gaps: number[] = []
  private currentMs: number

  constructor(options: AdaptiveGapThresholdOptions) {
    this.initialMs = options.initialMs
    this.minMs = options.minMs
    this.maxMs = options.maxMs
    this.resumeGraceMs = options.resumeGraceMs
    this.multiplier = options.multiplier ?? 2
    this.minLearnableGapMs = options.minLearnableGapMs ?? 150
    this.historySize = options.historySize ?? 20
    this.minObservations = options.minObservations ?? 2
    this.currentMs = this.initialMs
  }

  /** The gap currently required to end a burst. */
  get thresholdMs(): number {
    return this.currentMs
  }

  /** A gap inside a burst that activity resumed across. */
  observeGap(gapMs: number) {
    if (gapMs < this.minLearnableGapMs) return

    this.gaps.push(gapMs)
    if (this.gaps.length > this.historySize) this.gaps.shift()
    if (this.gaps.length < this.minObservations) return

    const sorted = [...this.gaps].sort((a, b) => a - b)
    const p90 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.9))]
    this.currentMs = Math.min(this.maxMs, Math.max(this.minMs, Math.round(p90 * this.multiplier)))
  }

  /**
   * Activity resumed `gapMs` after the last activity of a burst this
   * threshold closed. Returns true when that resume was soon enough to count
   * as the same burst — the gap is then learned as within-burst.
   */
  observeResume(gapMs: number): boolean {
    if (gapMs > this.currentMs + this.resumeGraceMs) return false

    this.observeGap(gapMs)
    return true
  }

  /** Forget the learned profile (e.g. a new source). */
  reset() {
    this.gaps = []
    this.currentMs = this.initialMs
  }
}
